import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { GIFTS_PATH, giftKeys } from '@/hooks/queries/gift-keys';
import api from '@/libs/axiosInstance';
import { ErrorCode, type ApiResponse, type GiftReofferReview } from '@fluenceur/types';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

/**
 * The brand's read-only catalog check before it can create a fresh proposal.
 * It deliberately has its own key: it is not the recipient's claim read and
 * must never write a product snapshot as a side effect of rendering.
 */
export const giftReofferReviewKey = (brandId: string, giftId: string) =>
  [...giftKeys.detail(brandId, giftId), 'reoffer-review'] as const;

const catalogRefreshCodes = new Set<ErrorCode>([
  ErrorCode.E_GIFT_REOFFER_CATALOG_INVALID,
  ErrorCode.E_GIFT_REOFFER_STOCK_OVERRIDE_REQUIRED,
  ErrorCode.E_GIFT_CATALOG_VERIFICATION_UNAVAILABLE,
]);

export const shouldRefreshGiftReofferReview = (code: ErrorCode | undefined) =>
  Boolean(code && catalogRefreshCodes.has(code));

/** Refreshes active review observers with the existing review key, not a second fetch path. */
export const refreshGiftReofferReviews = (
  queryClient: QueryClient,
  brandId: string,
  giftIds: string[]
) =>
  Promise.all(
    [...new Set(giftIds)].map((giftId) =>
      queryClient.invalidateQueries({ queryKey: giftReofferReviewKey(brandId, giftId) })
    )
  );

export const useGiftReofferReviewQuery = (giftId: string | null) => {
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';

  return useQuery<GiftReofferReview, Error>({
    queryKey: giftReofferReviewKey(brandId, giftId ?? ''),
    enabled: Boolean(brandId && giftId),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<GiftReofferReview>>(
        `${GIFTS_PATH}/${giftId}/reoffer-review`
      );
      if (!data.data) throw new Error('Invalid reoffer review response');
      return data.data;
    },
  });
};
